/**
 * Manager class for click, touch, pan and swipe events on a menu.
 * 
 * The expected menu HTML Element should have these properties:
 * - display: block;
 * - position: fixed;
 * - transform: translateY(calc(-100% + {menuHeight}px));
 * - will-change: transform;
 * - top: 0;
 * - z-index: 100;
 * 
 * Menu Height is the portion of the menu that is always visible
 * 
 * The following classes are set on the Menu element for animation:
 * - ease-in (close menu)
 * - ease-out (open menu)
 * - ease-in-out (not used but removed in case it is set)
 * 
 * Use registerHammerHandlers() after class construction.
 * Use menuOpen() and menuClose() to open and close the menu from a button.
 * Use menuState to see if the menu is open or closed.
 */

// menuState
export enum MS {open, closed};

export class MenuManager {
  body             : HTMLBodyElement;
  menu             : HTMLElement;
  menuHeight       : number;
  lastMenuPosition : number;
  menuState        : MS;
  hammerManager    : HammerManager;
  closeHandler     : () => void;

  constructor (
    menuHeight: number,
    menuElement: HTMLElement,
    closeHandler?: () => void 
  ) {
    this.closeHandler     = closeHandler ? closeHandler : () => {};
    this.body             = <HTMLBodyElement> document.querySelector('body');
    this.menu             = menuElement;
    this.menuHeight       = menuHeight;
    this.lastMenuPosition = this.menuBasePosition();
    this.menuState        = MS.closed;
       
    // iOS hack 
    if(navigator.userAgent.match(/(iPad|iPhone|iPod)/g)) {
      this.body.style.cursor = 'pointer';
    }

    // IE hack
    this.menu.style.transform = this.menuTranslateBase();
  }

  registerHandlers() {
    // Initialize hammer.js
    this.hammerManager = new Hammer.Manager(this.menu, {
      recognizers: [
        [Hammer.Pan, { direction: Hammer.DIRECTION_VERTICAL }]
      ]
    });
    this.hammerManager.on('pan panend', this.panHandlerMenu);
    this.body.addEventListener('click', this.clickHandlerBody);
  }

  unregisterHandlers() {
    if (this.hammerManager) {
      this.hammerManager.off('pan panend');
      this.hammerManager.destroy();
      this.menu.style.touchAction = 'auto';
    }
    this.body.removeEventListener('click', this.clickHandlerBody);
  }

  menuTranslateBase() {
    const isIe = window.navigator.userAgent.indexOf('Trident') > 0;
    return isIe ? 
      `translateY(${this.menuBasePosition()}px)` : 
      `translateY(calc(-100% + ${this.menuHeight}px))`;
  }

  menuBasePosition = () => {
    return -this.menu.offsetHeight + this.menuHeight;
  }



  clickHandlerBody = (event: MouseEvent) => {
    if (this.menuState == MS.open) {
      this.menuClose('ease-in');
    }
  }

  panHandlerMenu = (event: HammerInput) => {

    // Exit if this is a mouse or pen event
    if (event.pointerType != 'touch') return;

    // New position
    const newPosition = this.lastMenuPosition + event.deltaY;

    // iOS Bug: Exit if pointer is not over menu
    if (event.pointers[0].clientY > newPosition - this.menuBasePosition() + this.menuHeight + 50) return;
    
    const openMenuPan =
      this.menuState == MS.closed &&
      event.offsetDirection == Hammer.DIRECTION_DOWN &&
      event.type == 'panend' &&
      (newPosition > (this.menuBasePosition() + 50));

    const openMenuSwipe =
      this.menuState == MS.closed &&
      event.offsetDirection == Hammer.DIRECTION_DOWN &&
      event.type == 'panend' &&
      event.deltaY >= 10 &&
      event.velocityY >= 0.3;

    const openMenu =
      openMenuPan ||
      openMenuSwipe;

    const closeMenuPan = 
      this.menuState == MS.open &&
      event.offsetDirection == Hammer.DIRECTION_UP &&
      event.type == 'panend' &&
      newPosition < -350; // -50 for partner features

    const closeMenuSwipe = 
      this.menuState == MS.open &&
      event.offsetDirection == Hammer.DIRECTION_UP &&
      event.type == 'panend' &&
      event.deltaY <= -10 &&
      event.velocityY <= -0.3;

    const closeMenu =
      closeMenuPan ||
      closeMenuSwipe;

    const hasMaxSize = 
      newPosition >= 0;

    const isOnTop = 
      newPosition <= this.menuBasePosition();

      
    if (openMenu) {
      this.menuOpen('ease-out');
    }
    else if (closeMenu) {
      this.menuClose('ease-in');
    }
    else if (newPosition < 0 && newPosition > this.menuBasePosition()) {
      this.removeAnimations();
      this.menu.style.transform = `translateY(${newPosition}px)`;
      if (event.type == 'panend') {
        this.lastMenuPosition = newPosition;
      } 
    }
    else if (hasMaxSize) {
      this.menuOpen()
    }
    else if (isOnTop) {
      this.menuClose()
    }
  }

  menuClose(animation?: string) {
    this.menuState = MS.closed;
    this.lastMenuPosition = this.menuBasePosition();
    this.menu.style.transform = this.menuTranslateBase();
    this.removeAnimations();
    if (animation) this.menu.classList.add(animation);
    this.closeHandler();
  }

  menuOpen(animation?: string) {
    this.menuState = MS.open;
    this.menu.style.transform = 'translateY(0px)';
    this.lastMenuPosition = 0;
    this.removeAnimations();
    if (animation) this.menu.classList.add(animation);
  }

  removeAnimations() {
    this.menu.classList.remove('ease-in');
    this.menu.classList.remove('ease-out');
    this.menu.classList.remove('ease-in-out');
  }


}
